/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { map } from 'lodash';
import LoadingButton from '@mui/lab/LoadingButton';

import SelectField from 'common/forms/SelectField';
import FormTextField from 'common/forms/TextField';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import { formatZipCode, normalizePhoneNumber } from 'common/utils';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import {
  validateCombinedAddressLength,
  validatePhoneNumber,
  validateRequired,
  validateZipCode,
} from 'common/forms/formValidations';
import { validateShipmentAddressApi } from 'api/order';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { updateShipment } from 'store/thunks/workQueueThunks';

const STREET_ABBRS = {
  street: 'st',
  avenue: 'ave',
  boulevard: 'blvd',
  drive: 'dr',
  road: 'rd',
  court: 'ct',
  lane: 'ln',
  place: 'pl',
  circle: 'cir',
  terrace: 'ter',
  highway: 'hwy',
  parkway: 'pkwy',
  suite: 'ste',
  north: 'n',
  south: 's',
  east: 'e',
  west: 'w',
};

const normalizeStreet = (line = '') => {
  let s = line.toLowerCase().trim();
  Object.entries(STREET_ABBRS).forEach(([full, abbr]) => {
    s = s.replace(new RegExp(`\\b${full}\\.?\\b`, 'g'), abbr);
  });
  return s.replace(/\s+/g, ' ').trim();
};

const zip5 = (zip = '') => zip.replace(/\D/g, '').slice(0, 5);

const isSubstantiallyDifferentAddress = (newAddr, existingAddr) => {
  if (!existingAddr || !newAddr) return false;
  return (
    normalizeStreet(newAddr.line1) !== normalizeStreet(existingAddr.line1) ||
    (newAddr.city || '').toLowerCase().trim() !== (existingAddr.city || '').toLowerCase().trim() ||
    (newAddr.state || '').toLowerCase().trim() !==
      (existingAddr.state || '').toLowerCase().trim() ||
    zip5(newAddr.zip) !== zip5(existingAddr.zip)
  );
};

const EditShipmentAddressDialog = ({ open, onClose, shipment, onAddressUpdated }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [addressText, setAddressText] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isAddressValidated, setIsAddressValidated] = useState(false);
  const [lastValidatedAddress, setLastValidatedAddress] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [suggestedAddressesOpen, setSuggestedAddressesOpen] = useState(false);
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const [selectedSuggestedIndex, setSelectedSuggestedIndex] = useState(0);

  const initialValues = useMemo(() => {
    if (!shipment?.shipmentAddress) return {};

    const address = shipment.shipmentAddress;
    return {
      fullName: shipment.recipientName || '',
      line1: address.line1 || '',
      line2: address.line2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      phone: shipment.shipmentPhoneNumber || '',
    };
  }, [shipment]);

  useEffect(() => {
    if (open && shipment?.shipmentAddress) {
      const { line1, line2, city, state, zip, isVerified, addressValidity } =
        shipment.shipmentAddress;
      const isValid = addressValidity
        ? addressValidity === 'VALID' || addressValidity === 'WARN'
        : Boolean(isVerified);
      setIsAddressValidated(isValid);
      if (isValid) {
        setLastValidatedAddress({
          line1: line1 || '',
          line2: line2 || '',
          city: city || '',
          state: state || '',
          zip: zip || '',
        });
      } else {
        setLastValidatedAddress(null);
      }
    } else {
      setIsAddressValidated(false);
      setLastValidatedAddress(null);
    }
  }, [open, shipment]);

  const handleClose = () => {
    setAddressText('');
    setIsAddressValidated(false);
    setSuggestedAddressesOpen(false);
    setSuggestedAddresses([]);
    onClose();
  };

  const handleSelectSuggestedAddress = (form) => {
    if (
      !form ||
      selectedSuggestedIndex < 0 ||
      selectedSuggestedIndex >= suggestedAddresses.length
    ) {
      return;
    }

    const validatedAddress = suggestedAddresses[selectedSuggestedIndex];
    form.change('line1', validatedAddress.line1);
    form.change('line2', validatedAddress.line2 || '');
    form.change('city', validatedAddress.city);
    form.change('state', validatedAddress.state);
    form.change('zip', validatedAddress.zip);

    const suggestedValidity = validatedAddress.validity;
    const isSuggestedValid = suggestedValidity === 'VALID' || suggestedValidity === 'WARN';
    setIsAddressValidated(isSuggestedValid);
    setLastValidatedAddress({
      line1: validatedAddress.line1 || '',
      line2: validatedAddress.line2 || '',
      city: validatedAddress.city || '',
      state: validatedAddress.state || '',
      zip: validatedAddress.zip || '',
    });
    setSuggestedAddressesOpen(false);
    setSuggestedAddresses([]);
    enqueueSnackbar('Address updated with selected suggestion', { variant: 'success' });
  };

  const formatAddressLine = (addr) => {
    const parts = [addr.line1];
    if (addr.line2) parts.push(addr.line2);
    parts.push(`${addr.city}, ${addr.state} ${addr.zip}`);
    return parts.join(', ');
  };

  const handleParseAddress = (text) => {
    if (!text || !text.trim()) return null;

    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length === 0) return null;

    const parsed = {
      fullName: lines[0] || '',
      line1: lines[1] || '',
      city: '',
      state: '',
      zip: '',
      phone: '',
    };

    const cityStateZipPatterns = [
      /(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/,
      /(.+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/,
    ];
    if (lines.length > 2) {
      const candidates = [lines[lines.length - 1], lines[lines.length - 2]].filter(Boolean);
      candidates.some((line) =>
        cityStateZipPatterns.some((re) => {
          const match = line.match(re);
          if (match) {
            const [, city, state, zip] = match;
            parsed.city = city.trim();
            parsed.state = state.toUpperCase();
            parsed.zip = zip;
            return true;
          }
          return false;
        })
      );
    }

    const phoneNumberRegex = /^[\d\s().-]+$/;
    const phoneLikeLine = lines.find(
      (line) => phoneNumberRegex.test(line) && (line.match(/\d/g) || []).length >= 10
    );

    if (phoneLikeLine) {
      parsed.phone = phoneLikeLine.trim();
    }

    return parsed;
  };

  const handleValidateAddress = async (form) => {
    const { values } = form.getState();
    if (!values.line1 || !values.city || !values.state || !values.zip) {
      enqueueSnackbar('Please fill in required address fields before validating', {
        variant: 'warning',
      });
      return;
    }

    setIsValidating(true);
    try {
      const rawResults = await validateShipmentAddressApi({
        input: {
          line1: values.line1,
          line2: values.line2 || '',
          city: values.city,
          state: values.state,
          zip: values.zip,
        },
      });

      // Flatten AddressValidationResult into flat address objects for UI consumption
      const addresses = rawResults?.map((result) => ({
        ...(result.resolvedAddress || result.originalAddress || {}),
        validity: result.validity,
        deficiencies: result.deficiencies,
        addressDirectoryId: result.addressDirectoryId,
      }));

      if (addresses && Array.isArray(addresses) && addresses.length > 0) {
        if (addresses.length === 1) {
          const validatedAddress = addresses[0];
          form.change('line1', validatedAddress.line1);
          form.change('line2', validatedAddress.line2 || '');
          form.change('city', validatedAddress.city);
          form.change('state', validatedAddress.state);
          form.change('zip', validatedAddress.zip);

          const { validity } = validatedAddress;
          const isValid = validity === 'VALID' || validity === 'WARN';
          setIsAddressValidated(isValid);
          setLastValidatedAddress({
            line1: validatedAddress.line1 || '',
            line2: validatedAddress.line2 || '',
            city: validatedAddress.city || '',
            state: validatedAddress.state || '',
            zip: validatedAddress.zip || '',
          });

          const getSnackVariant = () => {
            if (validity === 'VALID') return 'success';
            if (validity === 'WARN') return 'warning';
            return 'error';
          };

          const getSnackMessage = () => {
            if (validity === 'VALID') return 'Address validated successfully';
            if (validity === 'WARN') return 'Address validated with warnings';
            // INVALID or BLOCKED: show the actionable reason from the backend instead of a
            // generic "Invalid address" that doesn't say what to fix.
            return validatedAddress.deficiencies?.join(' ') || 'Invalid address';
          };
          enqueueSnackbar(getSnackMessage(), { variant: getSnackVariant() });
        } else {
          setSuggestedAddresses(addresses);
          setSelectedSuggestedIndex(0);
          setSuggestedAddressesOpen(true);
        }
      } else {
        setIsAddressValidated(false);
        enqueueSnackbar('Address validation returned no results', { variant: 'warning' });
      }
    } catch (error) {
      setIsAddressValidated(false);
      dispatch(handleError({ error }));
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = (formData) => {
    if (!shipment?.shipmentId) {
      enqueueSnackbar('Shipment ID is missing', { variant: 'error' });
      return;
    }

    setIsSaving(true);
    const input = {
      shipmentId: shipment.shipmentId,
      referenceId: shipment.referenceId,
      shipTo: {
        nameOnAddress: formData.fullName || shipment.recipientName,
        phoneNumber: formData.phone || shipment.shipmentPhoneNumber || '',
        line1: formData.line1,
        line2: formData.line2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
      },
    };

    const newAddr = {
      line1: formData.line1,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
    };
    if (
      isSubstantiallyDifferentAddress(newAddr, shipment?.shipmentAddress) &&
      shipment?.enableInsurance === true
    ) {
      input.enableInsurance = false;
    }

    dispatch(updateShipment({ input }))
      .then((updated) => {
        if (updated) {
          const updatedShipment = {
            shipmentId: updated.shipmentId,
            referenceId: updated.referenceId,
            cost: updated.cost,
            recipientName: updated.shipTo.nameOnAddress,
            shipmentAddress: {
              line1: updated.shipTo.line1,
              line2: updated.shipTo.line2,
              city: updated.shipTo.city,
              state: updated.shipTo.state,
              zip: updated.shipTo.zip,
              isVerified: updated.shipTo.isVerified,
              addressValidity: updated.shipTo.addressValidity,
              addressDeficiencies: updated.shipTo.addressDeficiencies,
              addressDirectoryId: updated.shipTo.addressDirectoryId,
            },
            shipmentPhoneNumber: updated.shipTo.phoneNumber,
            enableSaturdayDelivery: updated.enableSaturdayDelivery,
            ...(updated.validatedShipTo && {
              validatedShipmentAddress: {
                line1: updated.validatedShipTo.line1,
                line2: updated.validatedShipTo.line2,
                city: updated.validatedShipTo.city,
                state: updated.validatedShipTo.state,
                zip: updated.validatedShipTo.zip,
                addressValidity: updated.validatedShipTo.addressValidity,
                addressDeficiencies: updated.validatedShipTo.addressDeficiencies,
                addressDirectoryId: updated.validatedShipTo.addressDirectoryId,
              },
            }),
          };
          if (typeof onAddressUpdated === 'function') {
            onAddressUpdated(updatedShipment);
          }
        }
        enqueueSnackbar('Address updated successfully', { variant: 'success' });
        handleClose();
      })
      .catch((error) => {
        // Same swallowed-error bug fixed in CreateManualShipmentDialog.js: a BLOCKED address (or
        // any other backend validation failure) was refused with no reason shown here.
        enqueueSnackbar(getGraphQLErrorMessage(error), { variant: 'error' });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='lg'
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='h6' fontWeight={600}>
            Edit Ship To Address
          </Typography>
          {shipment?.referenceId && (
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
              Shipment ID: {shipment.referenceId}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label='Close'
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Form
          initialValues={initialValues}
          onSubmit={handleSave}
          render={({ handleSubmit, form }) => {
            const { values, hasValidationErrors } = form.getState();
            const currentAddressSnapshot = {
              line1: values.line1 || '',
              line2: values.line2 || '',
              city: values.city || '',
              state: values.state || '',
              zip: values.zip || '',
            };

            if (
              lastValidatedAddress &&
              (lastValidatedAddress.line1 !== currentAddressSnapshot.line1 ||
                lastValidatedAddress.line2 !== currentAddressSnapshot.line2 ||
                lastValidatedAddress.city !== currentAddressSnapshot.city ||
                lastValidatedAddress.state !== currentAddressSnapshot.state ||
                lastValidatedAddress.zip !== currentAddressSnapshot.zip)
            ) {
              // Address fields have changed after validation; reset validation status
              setIsAddressValidated(false);
              setLastValidatedAddress(null);
            }
            const isSaveDisabled =
              isSaving ||
              hasValidationErrors ||
              !values.fullName ||
              !values.line1 ||
              !values.city ||
              !values.state ||
              !values.zip ||
              !values.phone;

            return (
              <form onSubmit={handleSubmit} noValidate>
                <Box sx={{ p: 4 }}>
                  <Grid container spacing={4}>
                    {/* Left Column - Address Form */}
                    <Grid item xs={12} md={7}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant='h5' fontWeight={600}>
                          Ship To
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormTextField
                            label='Full Name'
                            name='fullName'
                            required
                            id='EditShipmentAddress-fullName-input'
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormTextField
                            label='Address Line 1'
                            name='line1'
                            required
                            id='EditShipmentAddress-line1-input'
                            validations={[validateCombinedAddressLength('line1', 'line2')]}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormTextField
                            label='Address Line 2'
                            name='line2'
                            id='EditShipmentAddress-line2-input'
                            placeholder='Address line 2'
                            validations={[validateCombinedAddressLength('line1', 'line2')]}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormTextField
                            label='City'
                            name='city'
                            required
                            id='EditShipmentAddress-city-input'
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <SelectField
                            label='State'
                            name='state'
                            required
                            id='EditShipmentAddress-state-dropdown'
                          >
                            {map(usStateAbbreviation, ({ name, abbreviation }) => (
                              <MenuItem key={abbreviation} value={abbreviation}>
                                {name}
                              </MenuItem>
                            ))}
                          </SelectField>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <FormTextField
                            label='ZIP Code'
                            name='zip'
                            required
                            type='zip'
                            maxLength={10}
                            format={formatZipCode}
                            validations={[validateZipCode()]}
                            id='EditShipmentAddress-zip-input'
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormTextField
                            label='Phone'
                            name='phone'
                            required
                            placeholder='XXX-XXX-XXXX'
                            id='EditShipmentAddress-phone-input'
                            helperText='Many carriers require a phone number for shipments.'
                            format={normalizePhoneNumber}
                            validations={[
                              validateRequired('Phone number is required'),
                              validatePhoneNumber('Invalid phone number'),
                            ]}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Right Column - Address Tools */}
                    <Grid item xs={12} md={5}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Copy & Paste Section */}
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <LightbulbIcon sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
                            <Typography variant='h6' fontWeight={600}>
                              Speed up with copy & paste
                            </Typography>
                          </Box>
                          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                            Paste or type address to fill out the form quickly.
                          </Typography>
                          <Typography variant='body2' fontWeight={500} sx={{ mb: 1 }}>
                            US Address
                          </Typography>
                          <TextField
                            multiline
                            minRows={4}
                            value={addressText}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setAddressText(newValue);
                              if (!newValue.trim()) {
                                // Paste area cleared – restore form to original shipment address
                                const addr = shipment?.shipmentAddress;
                                form.change('fullName', shipment?.recipientName || '');
                                form.change('line1', addr?.line1 || '');
                                form.change('line2', addr?.line2 || '');
                                form.change('city', addr?.city || '');
                                form.change('state', addr?.state || '');
                                form.change('zip', addr?.zip || '');
                                form.change('phone', shipment?.shipmentPhoneNumber || '');
                              } else {
                                const parsed = handleParseAddress(newValue);
                                if (parsed) {
                                  Object.keys(parsed).forEach((key) => {
                                    form.change(key, parsed[key] ?? '');
                                  });
                                }
                              }
                            }}
                            placeholder='Recipient Name&#10;4301 Bull Creek Rd&#10;Austin, TX 78731&#10;512-555-1234'
                            fullWidth
                            variant='outlined'
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.875rem',
                              },
                            }}
                          />
                        </Box>

                        {/* Original Address Section */}
                        {shipment?.originalShipmentAddress &&
                          (shipment.originalShipmentAddress.line1 !==
                            shipment.shipmentAddress?.line1 ||
                            shipment.originalShipmentAddress.line2 !==
                              shipment.shipmentAddress?.line2 ||
                            shipment.originalShipmentAddress.city !==
                              shipment.shipmentAddress?.city ||
                            shipment.originalShipmentAddress.state !==
                              shipment.shipmentAddress?.state ||
                            shipment.originalShipmentAddress.zip !==
                              shipment.shipmentAddress?.zip) && (
                            <Box
                              sx={{
                                p: 2.5,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                bgcolor: 'grey.50',
                              }}
                            >
                              <Typography variant='h6' fontWeight={600} sx={{ mb: 1.5 }}>
                                Original Address
                              </Typography>
                              <Box
                                sx={{
                                  pl: 2.5,
                                  borderLeft: `3px solid ${theme.palette.grey[400]}`,
                                }}
                              >
                                <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                                  {shipment.originalShipmentAddress.line1}
                                </Typography>
                                {shipment.originalShipmentAddress.line2 && (
                                  <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ mb: 0.5 }}
                                  >
                                    {shipment.originalShipmentAddress.line2}
                                  </Typography>
                                )}
                                <Typography variant='body2' color='text.secondary'>
                                  {[
                                    shipment.originalShipmentAddress.city,
                                    shipment.originalShipmentAddress.state,
                                    shipment.originalShipmentAddress.zip,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                </Typography>
                              </Box>
                            </Box>
                          )}

                        {/* Validate Address Section */}
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Typography variant='h6' fontWeight={600} sx={{ mb: 1.5 }}>
                            Validate and Autocorrect Address
                          </Typography>
                          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                            Use this tool to quickly validate and standardize the shipping address
                            before creating a label.
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              columnGap: 2,
                              rowGap: 1,
                              flexWrap: 'wrap',
                              mt: 0.5,
                            }}
                          >
                            <Button
                              variant='outlined'
                              onClick={() => handleValidateAddress(form)}
                              disabled={isValidating || isAddressValidated}
                              sx={{
                                textTransform: 'none',
                                minWidth: 110,
                                px: 2.5,
                                py: 0.5,
                              }}
                            >
                              {isValidating ? 'Validating...' : 'Validate'}
                            </Button>
                            {isValidating && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <CircularProgress size={16} />
                                <Typography variant='body2' color='text.secondary'>
                                  Validating…
                                </Typography>
                              </Box>
                            )}
                            {!isValidating && !isAddressValidated && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <WarningAmberIcon
                                  sx={{
                                    color: theme.palette.warning.warningColour,
                                    fontSize: '1.2rem',
                                  }}
                                />
                                <Typography
                                  variant='body2'
                                  sx={{ color: theme.palette.warning.warningColour }}
                                >
                                  Not validated
                                </Typography>
                              </Box>
                            )}
                            {!isValidating && isAddressValidated && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <CheckCircleIcon
                                  sx={{ color: 'success.main', fontSize: '1.2rem' }}
                                />
                                <Typography variant='body2' color='success.main' fontWeight={500}>
                                  Address validated
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <DialogActions
                  sx={{
                    px: 4,
                    py: 2.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    onClick={handleClose}
                    variant='outlined'
                    sx={{ textTransform: 'none', minWidth: 100 }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type='submit'
                    variant='contained'
                    color='primary'
                    loading={isSaving}
                    loadingPosition='center'
                    disabled={isSaveDisabled}
                    sx={{
                      textTransform: 'none',
                      minWidth: 160,
                    }}
                  >
                    {isSaving ? 'Verifying...' : 'Save & Verify'}
                  </LoadingButton>
                </DialogActions>

                {/* Suggested addresses dialog when validation returns multiple options */}
                <Dialog
                  open={suggestedAddressesOpen}
                  onClose={() => {
                    setSuggestedAddressesOpen(false);
                    setSuggestedAddresses([]);
                  }}
                  maxWidth='sm'
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      boxShadow: theme.shadows[10],
                    },
                  }}
                >
                  <DialogTitle
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant='h6' fontWeight={600}>
                      Select suggested address
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setSuggestedAddressesOpen(false);
                        setSuggestedAddresses([]);
                      }}
                      aria-label='Close'
                      sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'grey.100' } }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 2, px: 3 }}>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2, mt: 2 }}>
                      Multiple valid addresses were found. Choose the best match to update the form.
                    </Typography>
                    <RadioGroup
                      value={String(selectedSuggestedIndex)}
                      onChange={(e) => setSelectedSuggestedIndex(Number(e.target.value))}
                      sx={{ gap: 1, alignItems: 'flex-start' }}
                    >
                      {suggestedAddresses.map((addr, index) => (
                        <FormControlLabel
                          key={formatAddressLine(addr)}
                          value={String(index)}
                          control={<Radio />}
                          label={formatAddressLine(addr)}
                          sx={{
                            alignItems: 'flex-start',
                            width: '100%',
                            mx: 0,
                            '& .MuiFormControlLabel-label': { mt: 0.25 },
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </DialogContent>
                  <DialogActions
                    sx={{
                      px: 3,
                      py: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Button
                      onClick={() => {
                        setSuggestedAddressesOpen(false);
                        setSuggestedAddresses([]);
                      }}
                      variant='outlined'
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={() => handleSelectSuggestedAddress(form)}
                      sx={{ textTransform: 'none' }}
                    >
                      Select
                    </Button>
                  </DialogActions>
                </Dialog>
              </form>
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

EditShipmentAddressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddressUpdated: PropTypes.func,
  shipment: PropTypes.shape({
    shipmentId: PropTypes.string,
    referenceId: PropTypes.string,
    recipientName: PropTypes.string,
    shipmentPhoneNumber: PropTypes.string,
    deliveryInstructions: PropTypes.string,
    enableInsurance: PropTypes.bool,
    shipmentAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
      isVerified: PropTypes.bool,
      addressValidity: PropTypes.string,
    }),
    originalShipmentAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
  }),
};

EditShipmentAddressDialog.defaultProps = {
  shipment: null,
  onAddressUpdated: undefined,
};

export default EditShipmentAddressDialog;
